<template>
<div class="chapter">
<div class="inner_page">
<p class="c6" id="calibre_pb_5"><span class="c4">Chapter 1: The
Carrier</span></p>
<p class="c8"><a id="ch1"></a> <span class="c9">One</span></p>
<p class="western3">The carrier <i class="calibre4">Atlas</i> tore across the
cosmos, leaving a black wake of stardust.</p>
<p class="western3">It lurched forward with its rigid, hulking
hull, like a giant, tumorous tentacle, and the thrusters beamed
like a cluster of sapphires, gleaming with the stars in the endless
expanse.</p>
<p class="western3">Beneath the skin of the carrier mining ship,
the engineering decks housed the key to the stars, the Atlas' light
drive, which had begun to rumble and play its disquieting song,
spinning up to bend space like a rubber band. At full thrust, it
had taken the Atlas weeks to reach the edge of New Earth's solar
system, taxiing to minimum safe distance before it could engage the
drive and take the long stride faster than light toward its
destination, the outer reaches of the known galaxy, deep space.</p>
<p class="western3">The <i class="calibre4">Atlas'</i> crew called it "the
black," as if it were an amorphous being that could get inside and
drive them mad, and the pulse of the light drive didn't help. It
rose and fell in a mechanical hum like a wave of sound that bored
through their ears and into their minds. Its song resonated
throughout every deck, crawling up the maglev tram system, which
ran the length of the Atlas like a spine. The hum bellowed in the
Atlas' cargo bays, huge warehouse compartments in the carrier's
rib-cage-like belly.</p>
<p class="western3">The mechanical whine of the light drive
reached even as far as the residence decks at the fore where the
<i class="calibre4">Atlas'</i> chief security officer, Stellan Lund, woke from a
terrible dream as he did every sleep cycle, knowing he had
died.</p>
<p class="western3">With indiscernible cries still ringing in
his ears, he sat up in bed, feeling his heart pound. It clicked
behind his ears, and his head swelled so much it might burst. But
there was no pain, only the deep, outward pull of his arteries
opening wide, like his whole body breathed.</p>
<p class="western3">The air processing system whispered through
the vents that everything was all right, that it didn't matter
where his mind had taken him. He was back aboard the Atlas. He had
returned home.</p>
<p class="western3">The dream was recurring, and that was all he
knew about it because, even as he turned and pressed his bare feet
against the cold metal deck and the sensation shot up his legs like
electricity, the dream faded. But there was always a sense of
familiarity, that it had happened before, that he'd previously
visited those darkened corners of his mind. He could almost see it,
like his own reflection beyond the fog of a mirror. He couldn't
hold onto it, though; it faded until there was nothing.</p>
<p class="western3">From Stellan's personal data link that clung
to his wrist, a cascade of blue holographic panels leaped into the
air, and he found he had some time before his wake cycle began,
enough to get more sleep if he could coax his mind into returning
there.</p>
<p class="western3">He rubbed his eyes, doubtful that they would
stay closed for long, and when they opened, they fixed onto his
sidearm, a rail-fired HC30 heavy pistol, which hung in its holster
from the handle of his closet, always within easy reaching
distance. He traced the cool steel of the barrel with his
fingertips. As familiar to him as his own hand, Stellan's pistol
had been at his side for as long as he cared to remember. When he
touched the grooves on the grip where his fingers would fall, it
felt like reassurance from an old friend, and the final pit of fear
left his stomach.</p>
<p class="western3">He removed the weapon from its holster and
felt the significant weight, not too heavy or too light, but just
right. His hands had memorized the rubber grip and the steel
cylinder, the resistance it gave when he moved with it.</p>
<p class="western3">Stellan's sidearm had always been there for
him when he needed it most, when he had let his guard down, and
when he felt shame for exercising the most basic human instincts:
the will to survive.</p>
<p class="western3">That was another time, and he was another
person then. He no longer talked or even thought about his old
self. Nowadays, he and his sidearm spoke rarely, only in the firing
range to keep each other sharp, and they never mentioned their
history. Instead, they looked forward.</p>
<p class="western3">And then flames danced before his eyes.
Stellan tried to resist the heat out of disbelief. He raised his
hand to shield his face and turned away.</p>
<p class="western3">A city blazed. The skyline comprised
crumbling towers, and the first hints of dawn rolled over the
horizon.</p>
<p class="western3">A bullet whizzed past his ear, and he
sprinted to cover behind a burned out husk of a car. The back seat
still smoldered, and noxious fumes from the vinyl and cushioning
assaulted the back of his throat. He stifled a cough as he darted
behind rubble from a crumbling building. Glass crunched under
foot.</p>
<p class="western3">As he was trained, Stellan watched the high
ground, optimal locations for snipers, tracing the rooftops with
his deadly eye, the barrel of his MK7 Kruger assault rifle. He
looked at the places he would be if he wanted to pick off a few
Unity Corps soldiers, the New Earth Council's law enforcement unit.
The shadows of open and broken windows drew his attention. He
skipped the ones with flames behind them.</p>
<p class="western3">A hand pressed his shoulder.</p>
<p class="western3">Stellan realized his eyes were closed, and
when he opened them, the city vanished. The moan of the light drive
and the whisper of the air processors returned. Gentle fingers
wrapped over his shoulder, and he reached for them
instinctively.</p>
<p class="western3">Daelen's hand was cold, even though her
touch was warm. The dream and the city melted away like burning
cobwebs in a dark cave.</p>
<p class="western3">"You all right, love?" Even with his wife's
voice a whisper, its soft inflections and drawn out vowel sounds
reminded Stellan she'd grown up near London. Of all the things he
loved about her, he loved the way she sounded the most, like her
lips were gentle with her words.</p>
<p class="western3">"Fine."</p>
<p class="western3">"Another dream?" she asked, her voice rising
with worry.</p>
<p class="western3">"Yes."</p>
<p class="western3">She fell back, the sheets splashing around
her. A concerned sigh escaped her mouth, almost as if she'd been
holding it back. As the medical officer aboard the Atlas, her mind
drummed up the worst biological reasons for his dreams. She had no
experience in psychology and didn't have much faith in willing
someone to get better. She believed in treatment, and she worried
her husband suffered from a tumor or perhaps a virus that attacked
the brain.</p>
<p class="western3">She sat up and rolled behind him, wrapping
her arms over his shoulders, her hands finding his. She pulled them
close to his chest and squeezed.</p>
<p class="western3">"Go back to sleep," Stellan said. "Our wake
cycles don't begin for another few hours."</p>
<p class="western3">"Not without you." Whatever was bothering
him, she wanted to hold onto him and become his anchor. She
couldn't heal his body this way, but perhaps she could comfort his
soul.</p>
<p class="western3">"I don't know if I can," he said.</p>
<p class="western3">"Just lay with me then."</p>
<p class="western3">Stellan longed to go back to her, to bury
his nose in her hair and breathe deep, and he had the similar
thought that if he just held onto the sound of her voice and the
smell of her hair, the way it spilled over her shoulders like ink,
maybe it would keep those dark thoughts at bay. After all, he was
exhausted. Sleep would be worth the risk.</p>
<p class="western3">So he turned and kissed her softly. They
rolled together, and he was gentle as he raised the sheets, wiggled
his arm under her neck, and crossed his other arm over her abdomen.
He kissed her shoulder and heard her lips part into a smile in the
dark.</p>
<p class="western3">Soon, they both returned to sleep, and
Stellan's dreams were worse than they'd been in years.</p>
<p class="c10"><span class="c11">Two</span></p>
<p class="western3">Stellan woke with screams once again
rattling his head. The voices morphed into the Atlas' emergency
alert and then to the wake alarm on his link, like a child tugging
at the fabric of his pants. Even as he woke and his mind grasped at
reality, the voices lingered.</p>
<p class="western3">He sought Daelen with his hand and found
only the warmth where she had been, her scent lingering in the
sheets. He lay for a moment, his nightmares fading, wondering where
she could have gone. He checked his link again to see if he had
overslept, and found he had not. She must have left early.</p>
<p class="western3">He stood and carried a yawn to the bathroom,
feeling happy to be awake even though exhaustion tried to anchor
him to the bed. A persistent droning in his mind yearned for sleep,
but he had enough of the restlessness for a while. His dreams would
have to wait for him to return, and he knew they would.</p>
<p class="western3">A blue light swelled around the doorframe
and mirror in the bathroom. In the reflection, his own eyes
appeared so ghostly blue that he thought for a moment they looked
lifeless. He'd seen that vacant stare before, when all the color
seemed to drain from the irises. And even though his eyes twitched
with his gaze, he thought it was an accurate depiction of how he
felt. Windows to the soul and all that.</p>
<p class="western3">Stellan slid the shower door aside and
entered. He opened its valves, and a steady stream of warmth spread
across his chest. He leaned forward on the wall and let the water
run over his head and down his back, across several long, jagged
scars, some wounds that just would not heal and had become so
deeply part of his being that they manifested themselves on his
body.</p>
<p class="western3">As he washed, he touched the long, sweeping
edge just under his rib cage where burning shrapnel had once
threatened his life. He recalled the pain, how remarkably little
there had been. Simply, a piece of metal had nearly cut him in two,
and he remembered the warmth of the blood flowing down his belly
and into his lap as he propped himself against a tree, still
firing, still fighting.</p>
<p class="western3">His fingers found another, a thick bloom of
scar tissue like a flower on his shoulder, where a bullet fired
from one of the most unexpected places had passed through and left
him awestruck at more than he could possibly bear.</p>
<p class="western3">Again, that was another time, but he asked
himself, if that were true, why did he continue to recall it? Why
did his mind relentlessly pound him with memories he no longer
cared to revisit? If he'd moved on, why did those thoughts remain
so close to him, lying in wait just under the surface of the
shallows until the waves came?</p>
<p class="western3">He returned to the bedroom, the blue glow
reflecting off the sheen of water still on his naked skin. After
Stellan finished drying himself, he slid open his closet door,
revealing several black officer uniforms hanging in line. He put on
a pair of pants and then dropped to the floor for his morning
pushups, his chest heaving and his arms pumping like pistons.</p>
<p class="western3">When he finished, he grabbed a white t-shirt
from the closet and pulled it over his head. With a chirp, the
Atlas announced someone's desire to enter.</p>
<p class="western3">"Come in," Stellan called.</p>
<p class="western3">The door opened, and the bright light from
the outer hall hurt his eyes. A large figure stood rigid with his
head almost reaching the top of the doorframe. Stellan finished
rolling his shirt over his chest and tucked it into his pants.</p>
<p class="western3">Stellan's pupils adjusted to the glare, and
he saw a black uniform like his. It was one of his men. Judging by
the silhouette's sheer size, it was Doug Fowler.</p>
<p class="western3">Stellan believed in symbols, and while Doug
had no formal training in anything that would be applicable to
security, he'd hired the big man for his inherent ability to
intimidate others with his physical size. Since coming aboard, Doug
hadn't had to use force, which was a testament to Stellan's theory.
Whether people didn't want any trouble or Doug frightened them into
shape, it worked.</p>
<p class="western3">"You're early," Stellan said.</p>
<p class="western3">"I was hoping we could talk."</p>
<p class="western3">"On the way," Stellan said. "Give me a
sec."</p>
<p class="western3">"You ever think of maybe turning some lights
on in here?"</p>
<p class="western3">Stellan reached into the closet, chose the
officer coat his hand first touched, and threw it over his
shoulders. A trail of bulbous silver buttons on his coat slashed up
to his heart and then diagonally to his throat. The stiff collar
stood straight up at the nape of his neck, bristling the tips of
his short blond hair. A blue stripe lined the sides of his pants
down to his black leather boots.</p>
<p class="western3">He stopped for a moment to look in the
mirror. Symbols, he thought. He hated the uniforms, but he couldn't
deny the authority they represented. Though, something was missing,
and it wasn't the Council patch he'd torn from the coat's
shoulder.</p>
<p class="western3">"Yeah, yeah. I'd fuck you," Doug said.
"You're beautiful. Let's go."</p>
<p class="western3">Stellan looked sternly at Doug and reached
for the last piece of his uniform: his sidearm. He wrapped the
holster around his waist and instantly recognized its weight, by
which he could even count the thirteen rounds in the magazine. It
sparkled in the bathroom light, which sensed no occupancy and
faded.</p>
<p class="western3">Stellan left his cabin. The light from the
hall cleaved the room in two as he offered himself to the ship and
its crew, and they gladly swallowed him whole.</p>
<p class="c10"><span class="c11">Three</span></p>
<p class="western3">The hallway outside Stellan's cabin
resonated with vibrant life. The clamor of the marching crew and
the bright lights replaced the drone of the silence and darkness of
his cabin. He found it disorienting.</p>
<p class="western3">He glanced down the main thoroughfare of the
residence deck, which was arterial in design with perpendicular
branches like veins. The walls bowed outward to make the space feel
wider, but the volume of people threatened to push them further.
Advertisements for products sold in the ship's stores and films
that showed on the lounge and recreation deck lined the walls,
along with safety messages and reminders: <i class="calibre4">Remember, safety isn't
just a goal. It's a state of mind.</i></p>
<p class="western3">The time for the Atlas' shift change had
come. The heads of Stellan's fellow shipmates bobbed and swayed,
some going to work, others returning to their cabins. It was hard
to tell who was coming and who was going because everyone looked
exhausted, and in twelve hours, it would happen again. The Atlas
had two shifts, maintaining New Earth's twenty-four hour clock.</p>
<p class="western3">Stellan finished buttoning up his coat and
checked his pockets one last time to be sure he had everything.
From bow to stern, the Atlas was several kilometers long, and shift
change was very much a commute. If he forgot something in his
cabin, it wouldn't be easy to return.</p>
<p class="western3">His hand landed on the butt of his weapon,
and it pulsed with warmth. The grooves meant to help him maintain a
steady grip pricked his fingers lovingly, begging him to remember
how it felt.</p>
<p class="western3">"So, I know you said in the meeting
yesterday that if anyone asks we should just tell them we haven't
gotten our destination yet," Doug said.</p>
<p class="western3">The two officers began walking toward the
back of the ship, blending in with the crowd.</p>
<p class="western3">The <i class="calibre4">Atlas</i>' people bottlenecked in
entryways and narrow passageways. Some stopped to chat with
acquaintances and friends, and Stellan and Doug politely asked them
to break it up and move along. The corridors met their capacity,
and the crowds slowed to compensate. Stellan thought, if the Atlas
had the ability to stretch to better handle the heightened flow of
the volume of its crew, it would have. But the <i class="calibre4">Atlas</i> was not
alive. It was a machine, and machines offered no such
flexibility.</p>
<p class="western3">"People are asking, and it's just eating you
up that you don't know," Stellan said. "And you were hoping, since
the Captain and I go back, maybe he let me in on the secret and I
could be a bit more forthcoming with details away from the
others."</p>
<p class="western3">Doug nodded, looking uncertain. "Something
like that."</p>
<p class="western3">"To be honest, Doug, I wish I could tell
you. I really don't know where we're going. The Captain's kept me
in the dark about it, too."</p>
<p class="western3">"Don't it bother you?"</p>
<p class="western3">"No," Stellan said. "He does what he does
for reasons he doesn't have to explain to us. His crew is his
priority, and he wouldn't do anything that would endanger us.
Everyone should know that. If they ask, tell them that."</p>
<p class="western3">"That your military training talking?"</p>
<p class="western3">"Chain of command is one thing, but this
isn't the military," Stellan said. "It's trust. Trust your
Captain."</p>
<p class="western3">They continued in silence, passing through
the residential deck security checkpoint, nodding at another
officer stationed behind a desk, following the animated holographic
signs that hung from the ceiling, displaying the destinations
ahead, their temperature, gravity, and pressure. All Stellan and
Doug needed to know was that the font color was green. They knew
the layout of the ship by heart, and green meant the environment
integrity hadn't been compromised. Yellow, orange, or red would
have gotten their attention.</p>
<p class="western3">Stellan knew Doug wasn't finished. It was in
the way Doug didn't speak and in the way he avoided eye contact.
Introspection was abnormal for him. He normally thought with his
mouth.</p>
<p class="western3">"What about that Council woman?" Doug said.
"You trust her?"</p>
<p class="western3">Stellan didn't answer because anything he
said in response would be a lie. No, he didn't trust her, and he
couldn't help but sigh. He hoped Doug wouldn't interpret it as a
response, but it was evident from Doug's hardening face that he
understood it as irritation.</p>
<p class="western3">"Go ahead and start your rounds, Doug,"
Stellan said, trying not to sound dismissive.</p>
<p class="western3">Doug stopped, and the crowd flowed around
him like water. Some of the crew bumped into his arms, not even
budging the big man. Doug appeared to feel a cocktail of surprise,
amazement, and vexation, which Stellan knew he'd process as
resentment.</p>
<p class="western3">"We'll talk later," Stellan said.</p>
<p class="western3">"Sure thing, Chief," Doug said with
contempt. As Doug lumbered away, Stellan remembered how much he
hated the isolation of authority. He couldn't tell Doug that the
abnormality of not knowing their destination set him off, too. He
couldn't tell Doug that the presence of a Council agent aboard the
Atlas was both irregular and, for reasons not many would
understand, alarming.</p>
<p class="western3">Turning to face the march to the tram and
onward to the bridge, he felt a sense of absorbing into the crowd,
but he knew better. He knew the things he carried—his uniform, his
weapon, his duty—separated him from them. Because he was their
protector, they would never accept him. They would never be
comfortable with him in the way they were comfortable with each
other. His responsibilities to protect them formed barriers, and
even as he passed and met eyes with neighbors, even as he smiled
and nodded, he knew they returned those sentiments out of a sense
of obligation. Few relationships he had on the ship were
legitimate, and most existed because of his badge, not in spite of
it.</p>
<p class="western3">He hated it, but it was the price he paid
for their safety. He often felt it was his penance to feel outcast.
He could only hope they trusted him. For some, trust was earned,
and he wondered if he'd had such an opportunity on their quiet
ship. He hoped one day for that chance. He also hoped he would not
let them down.</p>
<p class="western3">So he continued on, counting the bobbing
heads, watching for signs of danger and harm, because they were his
flock, and he was the shepherd, trying desperately to fit in.</p>
<p class="c10"><span class="c11">Four</span></p>
<p class="western3">The platform at the maglev tram station
seethed. The crowd swelled dangerously close to the edge,
threatening to spill into the magnetic bed. It was the kind of
crowd where someone would bump into you, and it wouldn't even faze
you. You'd understand. There was no room for the luxury of personal
space, and if you wanted a seat on the tram, you'd give up every
bit you had.</p>
<p class="western3">The holographic displays on the platform's
pillars informed them a twelve-car tram would arrive in three
minutes. The station manager, Robert Powell, dozed in a leaned-back
chair in his booth as his holoterminal blinked and covered his face
in blue light. Stellan knocked on the booth's glass, startling
Robert, who looked around in a panic and then smiled at Stellan in
embarrassment. Stellan shook his head with a smile of his own and
walked away with a friendly wave.</p>
<p class="western3">He carefully slithered between each warm
body toward the platform edge, trying to be as considerate as
possible. Some of the crew turned, angry that someone was squeezing
in front of them. Once they saw it was Stellan, their demeanor
changed in the way anyone hides scorn in the face of authority.</p>
<p class="western3">Standing on the yellow line at the edge of
the platform, he turned to face the crowd. Looking at either end,
he watched their knees to ensure everyone maintained safe
clearance. He watched their shoulders to ensure they kept their
balance.</p>
<p class="western3">Behind the crowd, a holographic monitor
projected New Earth's news from the wall, and Stellan read the
headlines scrolling in a ticker at the bottom. Anchorman Shelly
Sheltonson's relentless smile and perfect teeth reported that
sixteen people resisted arrest and opened fire on a Unity Corps
unit in the District of Australia. None survived. In the Canadian
Province, a family refused to surrender religious texts, violating
the Freedom From Religion Act. The parents were being processed,
and the children would be sent to reform school and already were
lined up to be adopted by a noble family that was loyal to the New
Earth Council. Terrorists staged attacks in the Mediterranean and
greater Europe. The Unity Corps was hot on the trail of the leader
of the organization that claimed responsibility.</p>
<p class="western3">Stellan knew the rebels never actually
claimed responsibility because the message was more important to
them than ownership. The Council had declared the war over years
ago. Somebody had forgotten to inform the rebels. That message was
clear to him, even though most of New Earth's citizens ignored
it.</p>
<p class="western3">A dull pain on his upper arm brought his
attention back to the platform.</p>
<p class="western3">"Yow!" he yelled, his hand reaching for the
hot spot near his shoulder. He looked down and found his friend,
Wendy Lin, one of the Atlas' engineers.</p>
<p class="western3">A clean canvas now, her blue jumpsuit would
later be covered in grease from servicing gravity cranes in the
cargo bays, and even now, old stains streaked her chest, shoulders,
and legs. They were especially dark at her knees and elbows.
Though, with her black hair tied into a ponytail at the base of her
skull, her clean face shined with the precious innocence of a
younger sibling, even with the shrewd twist across her brow and the
swelling of her jaw muscles as she grit her teeth. In her normally
narrow eyes, which were now merely slits, he found more fire.</p>
<p class="western3">"Where were you last night?" she asked. And
then she socked him in the shoulder again. Her fist impacted with
all the force of a tennis ball, a quick jolt with little weight
behind it.</p>
<p class="western3">"Would you stop it?" Stellan said, grabbing
both of her arms and moving her away from the platform edge.
"Daelen didn't feel well, so I stayed with her. I'm sorry I didn't
message you."</p>
<p class="western3">"Yeah, I bet you are," Wendy scoffed.</p>
<p class="western3">Stellan's attention returned to the crowd.
His duty was too important. Still, he was curious. On the outruns,
the crew played basketball in the empty cargo bays. Stellan and
Wendy were on the same team, but he had missed their game the
previous night.</p>
<p class="western3">"What did I miss?" Stellan asked.</p>
<p class="western3">"What do you care?" Wendy asked. "We might
as well consider you an alternate if you're going to keep missing
games."</p>
<p class="western3">Stellan put his hands in the air. "Hey,
don't bench me, coach!"</p>
<p class="western3">She shook her head in dismissal.</p>
<p class="western3">"Rick Fairchild played," she said with a
grumble.</p>
<p class="western3">"Rick? Really?"</p>
<p class="western3">"Apparently he played a lot in his day.
Can't run worth a damn, but he's a pretty good shot. He won it for
us at the last second."</p>
<p class="western3">"What was the score?"</p>
<p class="western3">"Forty-nine to forty-eight."</p>
<p class="western3">"Close game."</p>
<p class="western3">She glared at him. "Yeah, but we would have
destroyed them if our team captain had been there."</p>
<p class="western3">"You still won," Stellan said. "Close games
are more fun anyway."</p>
<p class="western3">"That's not the point."</p>
<p class="western3">"Oh?" Stellan said. "I thought having fun
was the point."</p>
<p class="western3">"No, I mean, that's not why I'm mad at
you."</p>
<p class="western3">Stellan understood, but he didn't feel much
like getting into it then. His curiosity had been satisfied, and
had appeased Wendy. He returned his attention to the crowd.</p>
<p class="western3">"What are you doing anyway?" she asked.</p>
<p class="western3">"Watching."</p>
<p class="western3">"Watching what?"</p>
<p class="western3">"Everyone."</p>
<p class="western3">"That's silly," she laughed, her body
loosening. "You can't watch everyone." It was good that they could
move past his absence at the game. That was what he liked most
about her. While she had attitude and her temper could flare
quickly, she didn't dwell on things.</p>
<p class="western3">"It's not as hard as you'd think. Most
people just have too narrow of a focus. They watch hands or faces,
and it's impossible to watch everyone's hands. I look at their
shoulders, their hips and knees. Those parts of the body move
first. They give away what a person's going to do. It's the same
idea we use in self-defense."</p>
<p class="western3">Wendy lifted one foot from the floor and
shook it, examining her bent knee leading the direction in which
her foot moved.</p>
<p class="western3">"Speaking of which, you have to show me some
moves some time," she said, bouncing.</p>
<p class="western3">"Why?"</p>
<p class="western3">"In case I ever need to know."</p>
<p class="western3">"Do yourself a favor," he said. "If there's
ever trouble, run. Fighting will get you hurt, no matter how good
you are."</p>
<p class="western3">"Don't you think that's a little
hypocritical coming from someone who fights for a living?"</p>
<p class="western3">"My job is to prevent and resolve conflict,"
Stellan said. "Fighting only makes things worse."</p>
<p class="western3">"I've seen you fight."</p>
<p class="western3">"You've seen me defend myself from people
who are out of line."</p>
<p class="western3">"There's a difference?"</p>
<p class="western3">"The difference is I'm supposed to be in
that situation, not you."</p>
<p class="western3">"What if there's nowhere to run? What if I'm
trapped?"</p>
<p class="western3">Stellan couldn't imagine a scenario on the
Atlas where Wendy would be trapped or would ever need to know how
to defend herself. He knew learning self-defense bred
overconfidence; he also knew that meeting rising conflict led to
terrible places. He wanted to protect her from that. It was more
than just his job. As a friend, it was his duty to protect her.</p>
<p class="western3">"Don't worry," Stellan said, forgetting the
crowd and looking her in the eyes. He put his hand on her shoulder.
"You're safe here. No one's going to hurt you."</p>
<p class="western3">They shared a moment of silence where
Stellan couldn't be sure if Wendy doubted her safety because she
doubted him or if something had happened. She looked disappointed,
not reassured.</p>
<p class="western3">Violence had never brought him anything
worth fighting over. Except for the times he was using his skills
to defend another, he felt like he could have resolved every
conflict he'd ever been involved in if he'd just walked away. At
some point, which he felt was late in his life, he'd learned that
lesson. When he'd turned his back, it felt to him like his life had
turned around with him.</p>
<p class="western3">The floor beneath them rumbled and then
became stable again as the magnetic bed activated. The whole room
tightened. The walls constricted.</p>
<p class="western3">The maglev tram hovered into the station
silently, emerging from the tunnel like a giant worm. Only the
linear motor at the front whispered as it winded down. Stellan held
out his hands sideways, flicking his fingers inward to tell the
crowd on the platform to back away from the edge.</p>
<p class="western3">When the tram stopped, the floor shuddered,
and the paddles in the magnetic bed slapped the belly of each car,
clamping them securely into place.</p>
<p class="western3">Then the doors parted, and people funneled
in. Stellan shrugged. There wasn't much more he could do, so he
ushered Wendy gently into the tram.</p>
<p class="western3">"I hate the tram," she said.</p>
<p class="western3">"It's all we've got," Stellan said. "Could
you imagine the alternative?"</p>
<p class="western3">"Point taken," she said as the doors closed.
"I hate walking."</p>
<p class="c10"><span class="c11">Five</span></p>
<p class="western3">It was early when Daelen walked into the
infirmary, a long room lined on one side with examination tables
arranged like cemetery plots. On the other side, workstations and
laboratories led back to her office. Beyond, another door led to
private exam, recovery, and operating rooms. The last room on the
medical deck was the morgue.</p>
<p class="western3">Daelen's shift wouldn't begin for a while,
and she hoped to have some time alone. Instead, Daelen found Margo
Tailan, the medical intern, asleep on one of the exam tables, her
white lab coat draped over her body, her elbows and hips jutting
like sharp peaks in a snowy landscape. Daelen felt slightly
disappointed that she wouldn't have the deck to herself, but a warm
smile curled her mouth anyway. She pinched her lips to contain her
laughter.</p>
<p class="western3">Seeing Margo asleep on the exam table
reminded Daelen of the time she spent as an intern. Margo looked
peaceful, but Daelen knew how her back would ache when she woke.
Those exam tables weren't meant for sleeping.</p>
<p class="western3">Back then, Daelen focused on her career,
granting herself no time to pursue personal pleasures, such as the
warmth of a man who might love her, and the time she spent as an
intern had rocketed by as if it had its own light drive.</p>
<p class="western3">She didn't let time pass her by so quickly
anymore. She could feel it with her mind, wanting to slip, and she
feared waking one morning and realizing all she had were the lives
she'd saved. That wouldn't be terrible; practicing medicine and
helping people fulfilled her sense of purpose, but she yearned for
something more. At some point in her life, she realized what she
wanted was not just to give people back their lives but to also
give her own life back to herself. The key to slowing down time,
she learned, was creating memories.</p>
<p class="western3">Daelen walked through the exam area toward
her office and the private rooms beyond. She grabbed her lab coat
from behind her office door and swung it over her head, placing her
arms in the holes. Wind from her flapping coattail blew several
short strands of her black hair out from her neat ponytail, and she
absently brushed them behind her ears. Out of her pocket, she drew
her reading glasses, small oval lenses attached to thin black rims,
and she sat at her desk, staring at her blank holoterminal.</p>
<p class="western1">Life is about creating memories, she thought.
It's about creating, not just holding onto life and keeping it in
this world for as long as you could, but actually creating it.</p>
<p class="western3">She lifted her left arm, and her link fanned
open several 3D holographic windows. She flipped them sideways and
found her personal folder. Every crewmember had a personal folder
on the Atlas' servers, retrievable solely on their own links, but
Daelen thought that, in this case, she might as well have marked
her folder "secret."</p>
<p class="western3">The smile fell from her mouth, and her
eyebrows pushed together in a sharp furrow, the wrinkles like fine
cracks in porcelain.</p>
<p class="western3">She pressed on the folder with her palm, and
it fanned open several files. She flipped them and found the file
marked "results" and threw it to her holoterminal. The terminal lit
up and projected a flat screen with text. Reviewing the results,
she realized she loved the document. She reached out absently,
attempting to touch it. If she could, it might become more real,
more memorable. For the first time, she wished it were paper,
something she never understood the value of until now.</p>
<p class="western3">Her fingertips pierced the holographic
image. It was as tangible as the idea it represented. That was to
say, the thought of motherhood burned in her mind, not yet in her
palms.</p>
<p class="western3">But for that, she was almost thankful. It
was safer this way, easier to control. She released a deep sigh of
relief when she realized she still had time. She wouldn't begin to
show for another few weeks, perhaps a month, which was when they
were scheduled to return to New Earth and when she'd return to the
surface of her home planet for good. Expectant mothers weren't
allowed to travel in space, nor were children.</p>
<p class="western3">She'd come to love her life on the
<i class="calibre4">Atlas</i>. Like everyone aboard, the freedom had drawn her to
the ancient halls of that forgotten carrier ship, yet she couldn't
wait to see her belly begin to bulge. The yearning to see her
child's face flooded her chest with warmth, like a deep yawn that
stubbornly would not be released.</p>
<p class="western3">While it was quiet in the lab and she had
some time to herself, she wanted to revel in the thought of
motherhood. She wanted to coddle something, so she held onto the
idea that, by focusing on her child, she was creating the memory of
its conception, a joyous time in her life she would remember
fondly.</p>
<p class="western3">She wrapped her arms around herself and
closed her eyes. She imagined looking down and seeing her abdomen
expand, and she began to hum. Her voice matched the note of the
Atlas' light drive, and it soothed her instead of irritating her.
The hum, to her, had always rubbed her temples like sandpaper, but
she began to hear it as a song, her mind filling in the gaps of the
melody.</p>
<p class="western3">The swelling of the hum rose and fell; her
chest heaved. The sound of the light drive resembled a mechanical
heartbeat, and she thought about her child's heartbeat. She yearned
to feel it, and she placed her hand on her stomach and continued to
hum with her eyes closed, rogue strands of her hair leaving her
ears and falling across her cheek. She didn't brush them back this
time.</p>
<p class="western3">Over and over, the hum rolled, swaying her
body as if it laid hands upon her hips, and her voice matched that
note. Her voice hung onto the hum like swinging from the limb of a
tree. She heard the song she would sing to her child. It was a
pleasant melody. She thought about maybe writing some words to it,
but no, that might spoil it.</p>
<p class="western3">"What song is that?" Margo asked wearily
from Daelen's office doorway, her eyes little more than a squint.
The melody left Daelen, and her eyes opened to find her hand still
on her belly, which was flat again.</p>
<p class="western3">"I don't know," she said. "Just something I
made up, I guess." She grabbed the results document from her
terminal and dragged it back to her link, the fan of documents
closing back into her wrist. Her eyes darted toward the doorway, a
desperate attempt to see Margo without turning, but the intern
rubbed her face and yawned, shuffling forward into the room and
into her chair at a small desk in the corner.</p>
<p class="western3">Daelen doubted Margo had seen her pregnancy
results, but she undoubtedly saw her close them and hurriedly pull
them from the terminal, the sure sign of someone attempting to hide
something. If she asked, Daelen had not prepared a lie.</p>
<p class="western3">"Those tables really aren't as comfortable
as they look," Margo said, wincing from a pain that shot through
her back. "What are you doing anyway?"</p>
<p class="western3">A lie. Daelen frantically searched for a
lie. She couldn't find one, so she decided to stall.</p>
<p class="western3">"What do you mean?" Daelen asked and
immediately realized she was asking for trouble because Margo
wasn't only observant; she was analytical. Daelen thought Margo
would make a great doctor someday, which is why she had chosen
Margo for the position from hundreds of applicants.</p>
<p class="western3">Margo rolled her head back with her eyes
closed, stretching her neck. Her collarbone jutted like a bridge
between her shoulders.</p>
<p class="western3">"You're in early, poring over a document,
which you stash away like you're hiding something."</p>
<p class="western3">No. Daelen had to stop her.</p>
<p class="western3">"You're humming, and you're holding your
hand on your abdomen, and you—oh my God, you're pregnant!" Margo's
eyes shot open, wide-awake and instantly alert.</p>
<p class="western3">Daelen hushed her, attempting to place her
finger over her lips, but she was so frustrated her fingers simply
balled into a fist.</p>
<p class="western3">"What if the Captain finds out!?" Margo
asked.</p>
<p class="western3">"I don't know. Why don't you go tell him?"
An awkward silence wiggled between them, and Margo flushed with
embarrassment, suspecting she'd overstepped the bounds of their
relationship. Margo revered her mentor. They had worked together a
lot since she started the internship weeks ago, the day the Atlas
last left New Earth. But she didn't think they were friends
yet.</p>
<p class="western3">"I'm sorry, Dr. Lund."</p>
<p class="western3">"It's all right." Daelen offered a
reassuring smile. Margo felt a little better, but then her own
self-interest set in. Like Daelen in her early years, Margo
obsessed over practicing medicine, and she began to worry about
what would happen to her internship if Daelen were grounded.</p>
<p class="western3">"You can't keep a file like that on the
server," Margo blurted. "I mean, should you keep a file like that
on the server?" Her voice rose in a forced innocent tone.</p>
<p class="western3">"Why not?"</p>
<p class="western3">"Someone might find it."</p>
<p class="western3">"Like who?"</p>
<p class="western3">"The Captain." Captain Gordon Pierce always
had a way of leaving a first impression of intimidation, but he was
a good man. A principled man.</p>
<p class="western3">"Please," Daelen said. "Gordon would never
snoop on his crew. It would violate his code."</p>
<p class="western3">"Stellan know?"</p>
<p class="western3">"No."</p>
<p class="western3">"When are you going to tell him?"</p>
<p class="western3">"I haven't decided."</p>
<p class="western3">"What does that mean?"</p>
<p class="western3">"It means I haven't decided."</p>
<p class="western3">"Haven't decided what?"</p>
<p class="western3">"Anything, really."</p>
<p class="western3">"Are you going to keep it?"</p>
<p class="western3">"I haven't decided," Daelen said with a sigh
that bordered on boredom. "That's enough about me. What's wrong
with you?"</p>
<p class="western3">Margo didn't want to talk about herself
because her future was through Daelen; however, she felt the
floodgate of information close on her teacher. Margo didn't want to
push any harder because she didn't want to insult Daelen. She
thought she should have been thankful Daelen had let her stick her
nose so far into such private matters.</p>
<p class="western3">"Nothing," she said. "Why?"</p>
<p class="western3">"You're sleeping on an exam table," Daelen
said, motioning with her arm.</p>
<p class="western3">"Oh yeah, that," Margo said, stretching her
back. "It was really supposed to be a short nap."</p>
<p class="western3">"That's usually how it starts," Daelen said,
crossing her legs and adopting the posture of an attentive
doctor.</p>
<p class="western3">"I've just been feeling run down," Margo
said. "Don't know why."</p>
<p class="western3">"Maybe you're working too much."</p>
<p class="western3">"I have been working a lot," Margo said with
a self-promoting smile. Daelen shook her head dismissively and
laughed.</p>
<p class="western3">"Even though I was exhausted, I just
couldn't sleep last night. Most nights, it seems, anymore," Margo
said with a sigh. "So I came down to do a little more work on those
blood samples we took from the crew as we were leaving orbit."</p>
<p class="western3">"Find anything?"</p>
<p class="western3">"Everyone is clean."</p>
<p class="western3">"Good. Look at them again, and when you're
ready, we'll examine them together." Margo turned eagerly toward
the microscope on her desk, which pinned down a slide with a blood
sample, as if holding it at gunpoint. She wiped her eyes again and
looked into the lenses.</p>
<p class="western3">It pleased Daelen to see the zeal with which
Margo went back to work, especially when the lesson she was
teaching her had nothing to do with the blood but the folly of
repeating a process too many times, of the inefficiency of too many
redundancies and safeguards. It was a lesson she'd better learn now
rather than later if she was going to practice on New Earth.</p>
<p class="western3">Anything looked wrong if you stared at it
long enough, and she hoped to force Margo into making a mistake or
at least thinking she'd made a mistake. There was time for her to
learn how to determine when the job was done and to walk away with
certainty and confidence.</p>
<p class="western3">Before she moved onto that lesson, however,
Daelen wanted to teach Margo something else about space living and
the human anatomy.</p>
<p class="western3">"No you don't. You," Daelen said and kicked
the back of Margo's chair. "Talk."</p>
<p class="western3">"I don't know. I've just been really tired
and haven't been able to sleep for the last couple of weeks. I get
headaches sometimes. And that hum," Margo said, pointing to the
ceiling, "makes it all worse."</p>
<p class="western3">Yes, Daelen thought, the pulse of the light
drive, the beautiful song of her unborn child. She cocked her head
sideways to listen, and she lost any affection she had for it. It
seemed different to her now. It seemed unnatural, like the rolling
sound of the siege weapons of Hell, and she felt dirty for ever
thinking otherwise.</p>
<p class="western3">"Sometimes, I wonder if it's something
serious," Margo said.</p>
<p class="western3">"You ever notice the Atlas has no windows?"
Daelen said passively, as if she hadn't even heard the last thing
Margo said. In fact, she didn't really pay attention because she
already knew what was wrong with Margo. "They'd be structural
weaknesses on the hull. There really isn't much to look at out
there anyway. Just black."</p>
<p class="western3">Margo looked around the lab and blinked with
confusion.</p>
<p class="western3">"There's no night and day out here, and our
bodies need that," Daelen said.</p>
<p class="western3">"Didn't they do something to the environment
systems that would help us maintain our circadian rhythms?
Something with the lighting?"</p>
<p class="western3">Daelen raised an eyebrow. "It helps, but to
varying degrees. I'm sure you're aware we all don't have the same
physiology."</p>
<p class="western3">"So why would they tell us they took care of
the problem when they really only mitigated it? That's very
deceiving."</p>
<p class="western3">"Yes," Daelen said. "It is."</p>
<p class="western3">Margo was quiet. She reflected on how Daelen
spoke ill of anything regarding New Earth and the Council that ran
it. She wanted to know what had gotten her mentor, a rising star in
the medical field, to forsake her career for a post that normally
was occupied by second-rate physicians and those who'd done
something to deserve punishment.</p>
<p class="western3">"Why do you stay here?" Margo asked. "I
mean, you could practice anywhere, yet you choose the Atlas.
Why?"</p>
<p class="western3">"Is love an adequate answer?" Daelen asked
and supposed not, since she didn't think Margo would understand it.
"You haven't practiced medicine on Earth, so I'm going to level
with you," Daelen said, leaning forward and placing her elbows on
her thighs. "It's boring. You're a mechanic following directions.
The first thing they give you at any practice or hospital is a
book, and then they tell you all the answers are in that book.
You're expected to follow it to the letter."</p>
<p class="western3">She bent to open a cabinet beside her desk
and ducked her head in. Some medical supplies fell out with the
sounds of plastic packaging crinkling. Her voice reverberated like
she was in a cave.</p>
<p class="western3">"The other part, perhaps the worst part, is
nothing is ever easy. Everything's complicated. No one uses
intuition anymore because that means liability, and because of
that, things tend to take much longer than they should. People get
sicker. People stay sick longer. Sometimes, it just makes sense to
jump to the obvious conclusions. And here, it's fairly obvious
you're not getting enough sun. You're probably also not getting
enough exercise, since you're here all the time. And, I know your
diet isn't exactly healthy."</p>
<p class="western3">She emerged from the cabinet and closed its
doors, two bottles of pills rattling in her hand.</p>
<p class="western3">"On Earth, we'd be running a gamut of tests,
and it might take weeks just to come to the conclusion I've already
come to because we always have to be one hundred percent certain of
a diagnosis. All the while, you're suffering from your symptoms.
Here, I can tell you to take these vitamin and melatonin
supplements, get some exercise, and let's see how you feel in a
couple of weeks. Your body is probably just having difficulty
adapting to this unnatural environment." A smile stretched her
face, but when Margo took her pills with wonder of the simplicity
of it all, Daelen's smile faded.</p>
<p class="western3">"That's it?" Margo asked.</p>
<p class="western3">"It's actually quite common out here. You
find ways to deal with it."</p>
<p class="western3">Daelen realized she loved these moments,
using her own mind to solve problems for patients. She loved
pulling out an answer and showing her patients everything would be
all right, even if it meant forgoing proper procedure. With a
child, she would be grounded and perhaps lose the privilege to
practice medicine altogether, everything she'd worked for her
entire life.</p>
<p class="western3">As much as her colleagues scoffed at where
she chose to practice, Daelen found value in its simplicity.
Unfortunately, it had become complicated.</p>
<p class="western3">Ultimately, she didn't know what she was
going to do. For the time being, she thought she would just have to
keep smiling. No one could know. Not yet.</p>
<p class="c10"><span class="c11">Six</span></p>
<p class="western3">The gravity cranes in cargo bay seventeen
boomed like hand grenades. They pumped their mechanical arms and
bobbed like oil rigs, stripping the very air. When the <i class="calibre4">Atlas</i>
reached its destination, the cranes would fire bolts of energy
designed to manipulate gravity and pull large bodies of earth into
the bays. Until then, the engineering crew serviced and cleaned
them, hoping to keep them pulling for another half century or more.
It was how they lived between worlds.</p>
<p class="western3">Wendy struggled against the bulkhead hatch
door, pushing with her shoulder and grunting. Even on well-oiled
hinges, the doors were almost too heavy for her to open. Finally,
the door swung wide, and the pounding of the gravity cranes struck
her like a blow to the head. She had forgotten to put on her
hearing protection, so her hands quickly covered her ears. She had
to abandon one to search through the small sack she carried on her
back for her earplugs. With her head tilted to her shoulder in an
effort to keep some of that eardrum-rattling noise out, she nimbly
wiggled her hearing protection into one ear and then the other in
between crane pounds.</p>
<p class="western3">The gravity cranes continued their rolling
onslaught, but they sounded far away, faded. The earplugs made it
bearable. More importantly, they protected her hearing. At a
distance, even within the bays, the cranes weren't so bad, though
their pounding was still eerie. Working so close to them and even
underneath them, the noise could blow eardrums.</p>
<p class="western3">"Stupid!" she cried, smacking herself on the
forehead.</p>
<p class="western3">When she reached to close the door, a hand
touched her arm. She turned, and the face she saw was as familiar
to her as her father's. Rough and unshaven, Rick Fairchild smiled.
The pallid skin of his long face and thin neck stretched up to his
dark, short hair, resembling a spent match.</p>
<p class="western3">"Allow me," he said.</p>
<p class="western3">"No!" Wendy shouted. "I got it!" She took a
deep breath, and the smell of the cargo bay, a mixture of grease
and coolant fumes, calmed her like coming home. It reminded her of
the repair shop her father had owned on New Earth, where she
learned she loved to build and fix machinery, an affinity that had
alienated her from the other girls and left her in the company of
middle-aged men who spit, cursed, and smoked but who brought her
presents on her birthday and listened when she started to fawn over
boys.</p>
<p class="western3">After remembering their faces, smiling even
through exhaustion and filth, pulling the door closed didn't seem
so hard. The door closed, the hinges turning easier that way, like
it wanted to close.</p>
<p class="western3">Rick playfully stretched her headphones
apart and placed them over her ears, pulled down the microphone
arm, and placed it near her mouth.</p>
<p class="western3">"Better?" he asked, grasping her small
shoulders. His voice sounded smoky in the radio with a touch of
static in the channel.</p>
<p class="western3">"Much," she said, and they exchanged a warm
smile.</p>
<p class="western3">"So what's it going to be, boss?" Wendy
asked, tightening her ponytail, the spotlighting in the bay
reflecting off her dark, sleek hair like water. "Where do you want
me?"</p>
<p class="western3">"How many times do I have to tell you all
I'm not your boss?" Rick said almost indifferently, placing a
cigarette rolling paper on the palm of his hand and tapping some
tobacco onto it.</p>
<p class="western3">"You're the senior."</p>
<p class="western3">"That's just the Captain's way of reminding
me I'm old," Rick said, rolling and sealing the cigarette with the
tip of his tongue. "One seventy-seven won't start; a cable in the
back is loose. No one is able to reach it."</p>
<p class="western3">Wendy pointed at him and hopped with
excitement. The fact of the matter was Rick knew what needed to be
done, and the rest of them knew he had the answers. So they
followed his lead. Whether he issued orders or not, he ran the
engineering crew, and everyone knew it but him.</p>
<p class="western3">"What?" Rick said. "Okay, I get it. Come
on."</p>
<p class="western3">"Yessir!" she said with an animated
salute.</p>
<p class="western3">They walked the line of gravity cranes as if
they were cannons on a castle rampart. The cargo bays each had ten
cranes, and they were numbered by the bay in which they were
located. Crane one seventy-seven was the eighth crane in bay
seventeen, if you counted crane zero, which had fallen beyond
repair and was awaiting replacement, scheduled for the next return
service at New Earth.</p>
<p class="western3">"I've been wondering for a while now," Wendy
said. "Why do you do that?" She pointed to the self-made cigarette
hanging in his mouth.</p>
<p class="western3">"What? Smoke?"</p>
<p class="western3">"No, roll your own."</p>
<p class="western3">"Because I never let anyone do for me what I
can do myself," Rick said. "And it's cheaper and tastes better. The
ones you can buy taste like dirty air."</p>
<p class="western3">Wendy grabbed the cigarette from between his
fingers and puffed it. She wasn't a smoker. Very few people smoked
anymore. The health risks had all been mitigated or become curable,
but it was an inconvenient habit because smoking wasn't allowed on
public property on New Earth. Smoking also wasn't allowed on the
Atlas, but a lot of what went on in the cargo bays was
overlooked.</p>
<p class="western3">She didn't like the cigarette taste in her
mouth, though she liked being around a smoker. It was another thing
that reminded her of her father's repair shop, along with the men
who disgusted most people. To her, the smoke and fumes in the air
weren't contaminants. They were elements of character and depth
only someone like her could appreciate and understand.</p>
<p class="western3">When Wendy and Rick passed crane one
seventy-four, it hissed, spewing a cloud of white smoke from
underneath. Then the manifold under the frame fell off, slamming
the deck with a solid, unyielding sound of heavy metal on metal. A
small shockwave set Wendy and Rick off balance; Wendy almost fell,
but Rick caught her arm.</p>
<p class="western3">"You idiot!" Thomas Foster yelled from the
back of the crane. Another man, Edward Stone, his jumpsuit bulging
at his midsection, his shoulders and bald head slumping, stumbled
away from the side of the crane. His eyes drooped, and he nervously
rubbed his arm.</p>
<p class="western3">"I'm sorry!" Edward shouted.</p>
<p class="western3">Beneath the crane, a dark liquid poured onto
the fallen manifold in spurts, like one of the machine's arteries
had been severed.</p>
<p class="western3">"You're so damned worried about 'where we're
going this time'," Tom said mockingly with a lazy, almost drunk,
sway of his head and hips, "that you forget to watch what the hell
you're doing."</p>
<p class="western3">"It's not that," Edward said timidly. "I
don't know."</p>
<p class="western3">"You don't know what?" Tom said. "We're
going to Hell, and there's nothing you can do about it. So, you
might as well hold on tight and do your job."</p>
<p class="western3">"Hang on a minute, Tom," Rick said.
"Accidents happen, and it's just lubricant. We have plenty of time
to fix this."</p>
<p class="western3">"Accidents? Plenty of time?" Tom said.
"There's enough work around here that I don't need someone to
create more for me. We'll be lucky to get this bay online at all,
and we've certainly had our share of accidents."</p>
<p class="western3">"Take a break," Rick said. "I'll help Edward
with this after Wendy and I tend to one seventy-seven."</p>
<p class="western3">For a moment, Rick thought Tom might
actually stay, but the man was eager to get to his second job of
holding down a barstool.</p>
<p class="western3">"Fine," Tom said. He dropped his tools on
the deck and walked off toward the end of the bay and the bulkhead
door with his familiar limp, an injury that reminded them all to be
ever mindful.</p>
<p class="western3">"Hey, boss," Edward said. Rick turned to
Edward patiently, containing his annoyance. "I don't mean to be a
nag or nothin', but where we going?"</p>
<p class="western3">"It doesn't matter, Eddie," Rick said.
"Every planet's the same. The universe's trash is our treasure."
Rick forced a smile, a pleasantry he reserved for someone whose
mind was slow, someone who wouldn't be able to tell the difference
between genuine friendliness and sympathetic compassion or even
pity. Though Edward didn't seem to notice the disingenuousness of
Rick's expression, he also didn't seem satisfied with the answer.
He stood still, timidly rubbing his forearm, a look of concern
wrinkling his round and aged face.</p>
<p class="western3">"Plug up that leak, Eddie," Rick said.</p>
<p class="western3">"Oh, right," Edward said. He jumped into
action, sliding on his back under the crane, his stomach pressing
against the edge of the frame.</p>
<p class="western3">"I'll be back in a few minutes."</p>
<p class="western3">Wendy and Rick continued down the line to
crane one seventy-seven. The last embers of Rick's cigarette
dimmed, and he flicked it out into the bay.</p>
<p class="western3">On cursory inspection, crane one
seventy-seven looked to be in good working condition. The frame and
panels shined, which was a rarity among cranes; even brand new
cranes didn't remain clean for long. Yet, it stood unmoving, a
long, dead arm hanging out into the wide-open bay, like a dead
man's finger breaking topsoil.</p>
<p class="western3">"Here we are," Rick said. "I think one of
those docking station all-stars at Earth tripped over something
during inspection." Rick and Wendy shared a quick laugh at Rick's
sarcasm. The dockhands at New Earth weren't as inept as the
engineering staff on the Atlas claimed, but it had become an inside
joke that unified them. If she were honest, Wendy might have
admitted that she tired of the joke, but it reinforced the crew's
unity, as if humans banding against other humans was a natural
behavior they couldn't deny.</p>
<p class="western3">They slid under the crouching four-legged
giant and pushed all the way to the back, where the crane was
nearly flush with the wall. They pulled themselves by the far lip
of the machine's frame, as if peering over a ledge, frightened by
what they might find.</p>
<p class="western3">"See it?" Rick asked.</p>
<p class="western3">"Oh," Wendy said in surprise, as if to
question that this minor problem was what these experts couldn't
fix. "Yeah."</p>
<p class="western3">She spun sideways and pushed her shoulder
against the wall. Her slender arm squeezed up behind the machine's
frame, and her fingers wrapped around the loose cable. She pushed
it in until it snapped into place.</p>
<p class="western3">"You get it?" Rick asked.</p>
<p class="western3">"Yeah."</p>
<p class="western3">They listened. Nothing happened.</p>
<p class="western3">"Let me see," Rick said, nudging Wendy out
of the way.</p>
<p class="western3">"Maybe it just needs a rest," Wendy giggled.
"Maybe it should sit this one out, too."</p>
<p class="western3">"That might put this whole cargo bay
offline," Rick said, grunting with frustration. "It wouldn't be the
first time, though."</p>
<p class="western3">"What do you mean?"</p>
<p class="western3">"I've seen pristine ships go adrift. I've
seen cranes fifty years past their prime outlast brand new ones.
And through all of it, for my money, the Atlas is the best carrier
in the fleet because it has one thing the others don't."</p>
<p class="western3">"What's that?" Wendy asked.</p>
<p class="western3">"Love."</p>
<p class="western3">Wendy laughed.</p>
<p class="western3">"I'm serious," Rick said. "This ship is home
for this crew, and the people here would die to protect it. I think
the Atlas knows that, so when those all-stars in Earth orbit forget
to order a part we need and promise to get it to us the next run,
things just seem to keep up anyway, to persevere like this old
bitch can fight through the pain."</p>
<p class="western3">"You talk like it's alive."</p>
<p class="western3">"It is."</p>
<p class="western3">Rick grunted and pulled down on the cable
until it would budge no more, wiggling the connector in its socket.
When that didn't work, he pulled a wrench from his belt and
violently banged the side of the frame. On his face, Wendy thought
she saw frustration and anger, but the crane knocked and slowly
rose into a roll of booms, joining the other cranes in the chorus
of chaos.</p>
<p class="western3">"Love, eh?" Wendy said.</p>
<p class="western3">"Sometimes it's tough love," Rick
admitted.</p>
<p class="western3">A pair of feet shuffled beside the crane,
and Edward Stone dropped to his hands and knees and peered at Wendy
and Rick.</p>
<p class="western3">"Hey, boss," Edward called. "The leak's
plugged, but I'm not feeling so hot. I think that's why I dropped
that manifold. Do you think I could bail for the day, too?"</p>
<p class="western3">"All right," Rick said. "But go see Dr.
Lund. Let her look you over. We're going to need you if we're going
to get these cranes in shape by the time we get there."</p>
<p class="western3">Edward shuffled away toward the end of the
cargo bay. When his headset was out of range, Wendy and Rick looked
at each other.</p>
<p class="western3">"Tom was right about one thing," Rick said.
"We certainly don't need any more accidents around here."</p>
<p class="c10"><span class="c11">Seven</span></p>
<p class="western3">The lift doors parted onto the command deck,
and Stellan stepped forward. From the lift lobby, which housed
multiple elevators to the command deck and other decks, a hallway
stretched. On both sides of the hallway, department managers sat at
workstations with holographic terminals, relaying information and
issuing commands to their subordinates throughout the ship. For a
moment, Stellan heard only the cacophonous knocks of their
fingertips against the glass keyboards as they typed messages and
entered commands, and he heard chirps, audial responses the
workstations announced as the department managers dragged and
dropped files between applications. It all amounted to a stereo
orchestra of chaos, but it meant order throughout the ship.</p>
<p class="western3">For Stellan, reporting for duty had become
more routine than eating breakfast, which he realized, since his
mind had been elsewhere, he had unintentionally skipped. First in
the Unity Corps and now as chief of security on the Atlas, he began
his days by checking in with his superior officer.</p>
<p class="western3">As he walked down the hallway toward the
bridge, the department managers were so absorbed in their work that
he didn't know if they noticed the sounds of his boot heels when he
passed. Their hands were capable, though. From the command center,
this group of men and women held the Atlas together. Both literally
and figuratively, the command center was the core of the ship, and
upon it, everything else spun.</p>
<p class="western3">"Give me a status update," said Council
Agent Adelynn Skinner from the bridge. She had boarded the Atlas in
New Earth orbit. The irregularity of her presence had been a
controversial topic of conversation among the crew. Most Council
agent activity was confined to New Earth, but agents had the
freedom to do essentially anything they wanted, answering only to
the Council itself. They were the Council's dark hand, operating in
shadows and secrecy, the necessary evils of a unified world.</p>
<p class="western3">Stellan knew more than most about the world
beneath that world. He saw beyond the agents' masks, and that
knowledge put him at great unease. Skinner wasn't to be
trusted.</p>
<p class="western3">Ahead of Stellan, the bridge loomed, a
brilliant white sphere, the command crew appearing to levitate.
Around them, the Atlas' interface system's 3D application windows
hovered in real space, enabling them to interact with the ship's
controls.</p>
<p class="western3">The agent faced the helm away from Stellan,
arms crossed and weight shifted onto one leg, her slender body's
posture crooked and unnatural. Her champagne hair poured down her
back.</p>
<p class="western3">Instinctively, Stellan's eyes were drawn to
the belt that hung around her waist, slanting and holding onto only
one hip. On the side that hung low, a handgun dangled precariously
in a holster, daring someone to try to disarm her. Even for
Stellan, though, it would have been a dangerous proposition.</p>
<p class="western3">She was allowed to carry a weapon. She also
was not required to wear a link, a fact that might have been to the
disadvantage of anyone else, but for her, it meant she couldn't be
tracked. If she could figure out ways to open doors on the ship
without a link, and Stellan was sure there were ways, she could be
like a ghost, moving anywhere and doing anything she pleased. That
thought disturbed him the most.</p>
<p class="western3">"Thrusters at seventy percent," said Arlo
Stone, the Atlas' pilot. His voice hung flat in the air,
monotonous, as if bored and reciting a script. "We have hull
integrity, and our EM field is holding. Light drive spinning up.
Like I said, the board is green."</p>
<p class="western3">The agent sighed. "Just tell me where we
are, Mr. Stone."</p>
<p class="western3">"We just passed the outer reaches of our
system," Navigator Cooper Evans interjected. "We'll be ready to
jump whenever the Captain is ready."</p>
<p class="western3">"Once we engage the light drive, how long
until we reach our destination?"</p>
<p class="western3">"I adjusted the course your 'navigational
specialists' charted while we were docked at Earth. Talk about
poster children for continuing education. I'm amazed they can
navigate their way out of their own shoes, and someone put them in
charge of charting a course for the deepest run any carrier has
ever made," Arlo said, shaking his head.</p>
<p class="western3">"Arlo," Skinner warned.</p>
<p class="western3">"I'm sorry, but the most direct route was
right in front of them. Sure, we have to shoot a gap between a star
and a singularity, but it's not rocket science."</p>
<p class="western3">"Actually," Evans said, "it is."</p>
<p class="western3">"You know what I mean."</p>
<p class="western3">Skinner rubbed her forehead. "Arlo."</p>
<p class="western3">"We should arrive a day early."</p>
<p class="western3">"Ahead of schedule," she said, blinking with
surprise. Her fingertips fell to her mouth and pinched her bottom
lip. "Good." Something in the tone of her voice suggested she
wasn't so pleased.</p>
<p class="western3">Skinner turned and walked out of the bridge,
an application window buzzing near her head. She swatted it
away.</p>
<p class="western3">The department managers looked disturbed by
her presence, like a wave of uneasiness as she passed. Some
flinched. One raised her shoulders like a breeze made her shiver.
Skinner's high-heeled boots ticked the metal deck like tap
shoes.</p>
<p class="western3">The iris in her left eye glowed with a
brilliant green, as if bejeweled with an emerald. It was beautiful
and captivating to the point that Stellan almost missed the deep
scar that trailed from the outer corner of her other eye. It
streaked back toward her ear like lightning, branching into a Y.
The iris in that eye was a pale blue, almost white, and Stellan
knew that meant it had been replaced.</p>
<p class="western3">He wondered how she could have survived a
wound like that, and then he thought she smiled at him. He could
have sworn there was a hint of a curl at the corner of her mouth
and a slight crease in her cheek.</p>
<p class="western3">He nearly stopped her to ask for her weapon,
but the crest of the New Earth Council emblazoned above her heart
reminded him of his place. Her uniform bore no other
identification, no name, service number, or rank, but the crest
signified military.</p>
<p class="western3">She passed Stellan like he was invisible,
walking straight and steady without diverting her course, while
Stellan yielded his shoulder. She entered the lift, and as the
doors closed, there was no mistaking her smile this time. It was
more than a greeting. Stellan saw something behind it, recognition,
perhaps a mischievous intent.</p>
<p class="western3">When the lift doors closed, he gazed at them
for a moment. He knew her from somewhere, had seen her before,
though he couldn't place her. An image taunted him from the back of
his mind. She was softer, with the unmistakable fear of
inexperience in her eyes, darting quickly from being on high alert,
but both of them green as copper fire, the kind of blaze that
consumes a city, and in the smoke that blanketed the streets,
someone screaming his name.</p>
<p class="western3">"Stellan?" Arlo said, calling down the
hallway. "Atlas to Stellan."</p>
<p class="western3">Stellan blinked at the closed lift door and
turned toward the bridge. Arlo was craning his head and looking at
him with a wrinkled brow.</p>
<p class="western3">"You all right, Chief?"</p>
<p class="western3">Stellan nodded and waved a hand in
dismissal. He continued past the department heads, who relaxed with
the familiar sounds of his rubber boot heels and his confident
stride. The atmosphere was returning to normal.</p>
<p class="western3">The circular mouth of the bridge door closed
behind him, and he stepped onto the opaque white glass walkway. The
bridge was a spherical room, the walls and platform ahead that same
thick glass. Behind it all, a white light emanated, and it was hard
to judge exactly how big the room was.</p>
<p class="western3">Stellan supposed the optical illusion was
intentional because the bridge was a complete holographic
environment. A small application window swooped over his shoulder,
greeting him with a chirp, inviting him to make a command. He
pressed upon the face of the window with his palm, and it shrank
into nothingness.</p>
<p class="western3">The walkway culminated in a round platform,
which was where Captain Pierce usually stood to command. Beside the
platform, ramps led down and around to the front of the room where
Arlo and Evans sat at their workstations.</p>
<p class="western3">Arlo removed his brimless cap, scratched his
shaved head, and replaced the cap above his brow. It wasn't cold on
the bridge. Arlo just always wore that hat. Stellan assumed it was
a comfort thing.</p>
<p class="western3">Beside Arlo, the blue glow of the
navigator's holographic interface reflected off Cooper Evans'
smooth, boyish cheeks and danced in his eyes like wildfire.</p>
<p class="western3">"The Captain was looking for you," Arlo
said.</p>
<p class="western3">"He isn't here?"</p>
<p class="western3">"He's already gone up to his cabin. And I'm
sure you saw the spooky Agent Skinner." He snorted and wiggled his
fingers in the air for melodramatic effect.</p>
<p class="western3">"Can't you ever take anything seriously?"
Evans said. "She is kinda spooky."</p>
<p class="western3">Arlo picked up a cushy ball from his
workstation and threw it at Evans. "Everything spooks you. You
watch too many movies."</p>
<p class="western3">"What did the Captain want?" Stellan
said.</p>
<p class="western3">Arlo shrugged. "I don't know. I just work
here."</p>
<p class="western3">Evans looked to Stellan with appealing eyes.
"Is there something wrong, Chief? With this run, I mean."</p>
<p class="western3">Arlo laughed. "Stel, tell Coop there's
nothing to worry about. He won't listen to me."</p>
<p class="western3">As with Doug earlier, Stellan wanted to tell
them that he didn't feel good about it. He wanted to share the
anxiety so that maybe they could reassure each other that
everything would be all right. That wasn't his place. It wasn't his
duty. His duty was to ensure their safety.</p>
<p class="western3">"Of course nothing's wrong," he said. "She's
just here because it's such a deep run, to be sure everything goes
smoothly."</p>
<p class="western3">"Yeah, but an agent on a carrier?" Evans
said. "Everyone is saying that's highly irregular."</p>
<p class="western3">"It isn't that irregular," Arlo said. "Is
it, Chief?"</p>
<p class="western3">Stellan paused longer than he would have
liked. "The Council does what it wants. Always has. Regularity is
irrelevant. You two should learn to just accept it," Stellan said
and winced at the lapse in his discretion. "But there would be no
point in endangering us, Cooper. Trust me. There's nothing to worry
about."</p>
<p class="western3">"Yeah, go change your diapers," Arlo said.
"The Captain wouldn't let someone on the ship if he thought there
was any danger. And if she does try to pull anything, the Chief
here will drop the hammer. No doubt about it." He playfully punched
Stellan's arm. It had more force than Wendy's blows earlier, but
without the discontent behind it, it somehow hurt less.</p>
<p class="western3">"What if the Captain didn't have a say?"
Evans said.</p>
<p class="western3">A small window expanded from Arlo's
workstation, framing an unshaven face lined with time. The man's
bespectacled eyes stared unflinchingly beneath eyelids that had
begun to overlap. Despite his apparent age, he exuded a kind of
power, which promptly stopped Arlo's assault on Stellan, and the
pilot righted himself at his workstation.</p>
<p class="western3">"Arlo," Captain Gordon Pierce barked, "has
the Chief checked in yet?"</p>
<p class="western3">"I'm here," Stellan said.</p>
<p class="western3">"Come to my cabin," Pierce said.</p>
<p class="western3">"Sir," Stellan said with a nod.</p>
<p class="western3">"Arlo, Ms. Skinner informs me we will arrive
at our destination a day early?"</p>
<p class="western3">"Yes, sir," Arlo said uncertainly. "Evans
and I found a shorter route those nincompoops at Earth missed."</p>
<p class="western3">"She says it could be dangerous. You think
it's the best course?" Pierce asked.</p>
<p class="western3">Arlo scoffed. "It isn't dangerous. The
radiation from the star won't be a problem, and while we may not be
rated to operate near event horizons, it's just a teensy-weensy,
little baby black hole. I know this ship better than anyone,
and—"</p>
<p class="western3">"Arlo."</p>
<p class="western3">The pilot sighed. "Yes. It's the best
course."</p>
<p class="western3">"Good work. Pierce out." The window shrank
into Arlo's workstation, winking out of existence.</p>
<p class="western3">"Well, I guess he didn't mind," Arlo
said.</p>
<p class="western3">"I can't believe you dragged me into that,"
Evans whined. "Now, if we die, he'll think I had something to do
with it."</p>
<p class="western3">"If we die, we'll be dead, and no one will
care."</p>
<p class="western3">"My mom would care," Evans pouted.</p>
<p class="western3">Stellan walked back down the narrow
passageway that led away from the bridge, to the lifts, and off the
command deck. One lift in the center reached as high as the
captain's private deck, and as Stellan moved toward it, he let the
bickering of the Atlas' two most influential technical operators
fade behind his ears and out of his mind.</p>
<p class="c10"><span class="c11">Eight</span></p>
<p class="western3">In Captain Gordon Pierce's quarters,
conflict hung in the air like a fog.</p>
<p class="western3">Pierce sat in an old-fashioned wooden chair
behind an oak desk, the scratches and gashes on the surface and
along the edges showing its age. Gaps between panels and around
drawers exposed the shrinkage of the wood over time. On the lip of
the desktop, Pierce's thumb rubbed nervously back and forth over
one particular spot, which had become a smooth indentation from
years of wear.</p>
<p class="western3">"You shouldn't have allowed one of your men
to change your ship's course without consulting you first," the
Council's agent said, meandering before the Captain's desk and
running her fingers over the spines of Pierce's book collection on
the shelves. She narrowly avoided a pile on the floor and stepped
over it respectfully, as if it were a corpse she didn't want to
disturb.</p>
<p class="western3">"It's what Arlo does," Pierce said.</p>
<p class="western3">"By allowing it to continue, you become
responsible for his insubordination," she said passively.</p>
<p class="western3">"My men need to know I have faith in their
abilities to do their duties," Pierce growled. His thumb stopped
rubbing, and he stood. Skinner faced him, still calm, pressing her
hands together into a triangle. "They don't get that with me
breathing down their necks. Command is about trust. I trust my men
to use their best judgment. And it's a two-way street." His high
and tight silver hair glinted like sparks.</p>
<p class="western3">"I used to believe something like that, but
no," the woman said, her green eye a bright flash of fire. "Command
is much simpler than that. It's about making your men follow
orders." She sauntered around his desk and leaned on its worn edge,
arching her back. Her breasts swelled beneath her tight black
top.</p>
<p class="western3">"It's almost a fight in itself. You find
their weaknesses, and then you exploit them," she said and leaned
closer. "The best leaders get their men to follow orders without
them knowing it," she whispered. "Some are better equipped for that
than others."</p>
<p class="western3">Pierce grabbed her shoulders, his forearms
bulging.</p>
<p class="western3">"I think the results speak for themselves,"
Pierce said through gritted teeth and pushed her away. "I gave my
men a destination and ordered them to get us there, and they're
getting us there a day ahead of schedule. That's called exceeding
expectations, and I didn't have to put a gun in their face."</p>
<p class="western3">The woman doubled over with laughter, and
her golden hair fell across her face like a shroud.</p>
<p class="western3">"Are these great leadership qualities of
yours why you're here commanding a carrier?" She brushed her hair
straight back over her head, allowing it to fall where it may.</p>
<p class="western3">"What is this really about, Adelynn?" Pierce
said, his voice trembling with sincerity. The color in her cheeks
faded. Her smile diminished.</p>
<p class="western3">Pierce and Agent Skinner hung onto each
other's gaze, like exhausted boxers embracing, ready to end their
toil but neither willing to let the other come out on top. They
knew someone always had to win, and even though it didn't matter
who the victor was, through sheer stubbornness, neither would cave.
Neither could give in any more than either of them could travel
through time and change the past. Yet, there was a softness to
their faces, a sense that they both wanted to let their differences
go. The longer their eyes refused to look elsewhere, the harder
they felt the pull, like gravity, toward forgiveness.</p>
<p class="western3">A knock on the hatch door broke that fragile
moment.</p>
<p class="western3">"Come in," Pierce called out. They silently
agreed to settle their differences later.</p>
<p class="western3">The metal scuffed as the door swung open,
casting a slice of light on the dim room. Stellan stepped forward
into the foyer of Pierce's cabin, a small living room with a couch,
a meeting table, and more books.</p>
<p class="western3">Pierce shot one final warning look toward
Adelynn and walked into his foyer to greet Stellan. Adelynn
followed.</p>
<p class="western3">"Stellan," Pierce said, "Ms. Skinner has
finally found it appropriate to share with us why she's here, where
we're going, and why the Council decided to send us there."</p>
<p class="western3">"Why now?" Stellan asked. Skinner offered no
response but an inquisitive look, with her bright green eye and her
other sickly, blue eye. Stellan wondered which had the ability to
peer into his soul. Pierce shook his head in frustration.</p>
<p class="western3">"Because we're out of comms range and can't
tell anyone," Stellan said.</p>
<p class="western3">"Correct," Skinner said. "You're as sharp as
I thought you'd be. Don't act surprised or feign insult. You both
know how secrets work."</p>
<p class="western3">"It doesn't mean we have to like it," Pierce
said.</p>
<p class="western3">"No, I suppose not," Skinner said. "Then
again, who cares what you like? Make no mistake, gentlemen. What I
tell you now is not out of some sense of obligation. You need to
know now so you can make proper preparations to do your job and
complete the mission."</p>
<p class="western3">Skinner had absolute power here, and the
Atlas was at her disposal. Still, Stellan and Pierce had an edge.
The Atlas required a crew to run it, and they were confident this
crew, their crew, would go nowhere under her direct command.</p>
<p class="western3">"Let's hear it then," Pierce said.</p>
<p class="western3">"Please, sit." She motioned toward the
conference table with a graceful hand. Pierce and Stellan took
seats opposite each other. Skinner sat adjacent to them. She waved
her hand over the table, and it lit up, projecting streaks of blue
light that appeared out of thin air and poured onto the tabletop
like falling steam.</p>
<p class="western3">The light settled, and a holographic planet
appeared. It rotated lazily, a soup of browns and reds looking like
nothing more than a mixture of mud and clay.</p>
<p class="western3">"This is the two hundred and fifty-ninth
identified celestial body in the Apophis system," Skinner said.
"This is where we're going. As you may know, Apophis is one of the
farthest systems we've been able to reach, and two five nine is out
farther than any planet we've ever mined. That alone makes this
mission special. The Atlas will set a record on this run." Stellan
and Pierce caught her mocking tone.</p>
<p class="western3">"We can handle it," Pierce said.</p>
<p class="western3">"Indeed," she said. "As you don't know, two
five nine has something we've never seen before, and the Council
wants it."</p>
<p class="western3">She reached forward and grabbed the
projection with both hands, a gesture Stellan thought looked much
like the Council's crest on her upper left breast, two hands
wrapping around New Earth. Then she pulled the projection apart,
and it separated into two halves, revealing an inner layer, which
was gray in the projection.</p>
<p class="western3">"Under the crust is a thick layer of an
unidentified material. There's an entire layer of the stuff wrapped
around the whole planet. Solid. The excavator <i class="calibre4">Shiva of the
Trinity</i> is on-site now and will break ground in the next few
hours."</p>
<p class="western3">"Why now? What's the rush?"</p>
<p class="western3">Skinner shoved two five nine aside, and a
big red ball crept into the view.</p>
<p class="western3">"Apophis, the star of this system, is a red
giant. Soon it will engulf its own system, and our precious cargo
will be gone forever. It's already dangerously close for our
ships."</p>
<p class="western3">"So you don't know what it is, but you plan
to rush to it, put it on our ship and have us take it to Earth,
banking on the hope that it's something good?" Stellan said.</p>
<p class="western3">"Yes."</p>
<p class="western3">"I'm sorry, but I can't allow that."</p>
<p class="western3">"Why not?"</p>
<p class="western3">Stellan had to be careful. "This is a
civilian ship," he pressed his index finger down on the table, "and
its crew depends on us to keep it safe and secure, to ensure they
return home in the same condition as they left. What you expect us
to do is just reckless. You already have us running out deeper than
we've ever been and dangerously close to a red giant, and now you
want to put some alien material on our ship? No."</p>
<p class="western3">"This is a Council ship, Mr. Lund," Skinner
said, "and to the Council, Apophis two five nine is the only planet
in the galaxy that matters right now. Think about the
possibilities. It could cure disease. It could be the secret to
eternal youth. It could be a new fuel that would extend the range
of our fleet tenfold. It could end instability and conflict on New
Earth."</p>
<p class="western3">"The war?" Stellan said, descending into
reflection. He closed his eyes and remembered. He didn't want to.
Flames danced before his eyes. The smell of smoke filled his
nostrils. Inexplicable anger rose from deep within him. "Or it
could make us all dead! Have you thought about that!?" He slammed
the table, and the image of Apophis fractured and distorted. It
disappeared and was replaced with a message informing them the
holotable had encountered a problem and needed to shut down. The
table chirped for their attention, requesting it be manually
restarted.</p>
<p class="western3">"We have," she said, "but if your crew
follows proper procedure, there should be no problems, should
there?"</p>
<p class="western3">She was right, and Stellan knew it. The
<i class="calibre4">Atlas</i> had protocols in place to prevent harm to its crew,
and as long as there were no accidents, there shouldn't be any
danger. As long as there were no accidents.</p>
<p class="western3">"Well," Skinner said, slapping her thighs.
"I suppose you two have some work to do." She stood and walked
toward the door.</p>
<p class="western3">"May I ask where you are going?" Pierce
said.</p>
<p class="western3">"Keeping tabs?"</p>
<p class="western3">"I just want to know where to find you if we
need you, since you declined to wear a link." Pierce tapped his
wrist.</p>
<p class="western3">"There's a stool with my name on it down at
the bar if you must know," she said. "Ingenious, by the way,
turning half of the mess into a full-service bar. If I were you,
I'd hope the Council doesn't find out about that." She pulled the
hatch, and the metal rubbed again, sending a shiver through their
eardrums.</p>
<p class="western3">Stellan stared across the table at Pierce,
and Pierce watched the door almost longingly as it closed behind
her.</p>
<p class="western3">"I really ought to have someone up here to
fix that door," Pierce said, gazing into another realm.</p>
<p class="western3">"We're working with unknowns here,
Gordon."</p>
<p class="western3">"It isn't the first time," Pierce said and
stood. He walked to a small table in the corner where a half-full,
unlabeled bottle of golden whiskey waited.</p>
<p class="western3">"Yeah, but we used to be able to get our own
recon if the brass shorted us on intel. Take every precaution we
could. This is just reckless," Stellan said.</p>
<p class="western3">"What do you want me to do?" Pierce asked,
picking up the bottle and removing its glass stopper. He smelled
the fumes, and a pleasant look crossed his face. "Good for
meditation," he said to no one in particular. He leaned the bottle
over on its side as a man might dip a woman in a dance. The head
spilled into the rocks glass.</p>
<p class="western3">"You know, we're out here running back and
forth, and I think all we need, all anyone needs, is a fine bottle
of whiskey to keep them warm at night," Pierce said. "You ever
think about the old days?"</p>
<p class="western3">"The Unity Corps?" Stellan asked.</p>
<p class="western3">"Yeah."</p>
<p class="western3">"I try not to."</p>
<p class="western3">"I remember some of those long, cold nights
in the field. We couldn't risk even starting a fire to warm our
hands. The wind would blow, and it was like Mother Nature was
taking bites right out of you, blowing the heat right off your face
like it was dust. The rain turned to needles, and you couldn't
shake the chill from your bones. We'd pass around that whiskey
that, who was it? Torrington? The whiskey that Torrington's father
made? And even though I'd tell you boys to shut your mouths, you'd
drink and tell jokes and stories and laugh. And all that heartache
would melt away. I didn't realize I loved that. Out here, there's
no wind. There's no rain. No adversary for our spirit, and I wonder
if we need that to feel alive sometimes."</p>
<p class="western3">When Stellan tried to recall those times, he
didn't think of the cold. He recalled heat and smoke. He remembered
the whole world burning.</p>
<p class="western3">"I want you to do your job," Pierce said.
"The best you can. I want you to keep an eye on her. We're playing
nice now, but we still have some leverage. She may not admit it
even to herself, but the crew won't take orders from her. She can't
fly this boat herself. She needs us, and I'm not about to risk the
Atlas or this crew. The first sign of trouble, and we're out of
there, regardless of what she says. I'll tie her up myself if I
have to." Pierce carried the glasses and the bottle to the table
and slid one to Stellan. The table continued to chirp, asking one
of them to reset it.</p>
<p class="western3">"I'm not about to see lives end out here,"
Pierce added and sipped his whiskey. He exhaled the heat through
clenched teeth. "If it comes to that, this time, I'll take the
responsibility when we get back."</p>
<p class="western3">"Why risk it at all?"</p>
<p class="western3">Pierce held another sip in his mouth and
thought for a moment, gazing into nothing.</p>
<p class="western3">"Maybe some risks are worth taking. Maybe
because I need to know I still can," he said. "And anyway, we don't
have much of a choice."</p>
<p class="western3">The two of them swirled their whiskey,
staring down into their glasses. Pierce laughed, and Stellan
understood the fear of losing his edge, of becoming as worn down
and soft as Pierce's ancient desk, as the old books he kept around
him. He supposed Pierce kept them because it reminded him what
happened when you let time and complacency rub against you for so
long. When Stellan put on his weapon at the beginning of every wake
cycle, it reminded him that he needed to be like it, ready to fire
when called upon. And the truth he had come to realize was he
wasn't sure whether he still could, whether the decay of age had
reached his spine and made it brittle.</p>
<p class="western3">"What if she's lying?" Stellan asked. "You
know she's lying about something. They never shoot straight with
people."</p>
<p class="western3">"She's here in a supervisory role only.
Mining crews have never been out this far. She's just here to help
make sure everything goes smoothly."</p>
<p class="western3">"You don't really believe that."</p>
<p class="western3">"I haven't the luxury of believing anything,
but I have to accept what she tells me."</p>
<p class="western3">"And what if she brings hell down on
us?"</p>
<p class="western3">"That's not going to happen. I won't let
it."</p>
<p class="western3">"Still," Stellan said, "what if?"</p>
<p class="western3">"We'll figure something out," Pierce said.
"Like always. We'll do what we have to do and find a way to live
with it."</p>
<p class="western3">Stellan took comfort in his Captain's
optimism, but he hadn't touched his whiskey and felt then that he
couldn't stomach it. He pushed the glass away and stood from the
table, pulling the hem of his overcoat to straighten the
wrinkles.</p>
<p class="western3">"I don't think I need to tell you the crew's
at a bit of unease," Pierce said. "With that woman aboard and our
destination a secret, I'd expect some of them to act a little out
of the ordinary. Take extra precautions. We've got enough on our
plate having to deal with Ms. Skinner. We don't need unrest among
the crew."</p>
<p class="western3">"I'm going to make my rounds," Stellan
said.</p>
<p class="western3">Pierce nodded, still staring into his glass.
Stellan opened the hatch door and barely noticed the scuff sound,
but it shook Pierce from his trance. Pierce watched his friend
leave and felt a sadness he couldn't understand, like seeing only
glimpses of a premonition of dark times. It nagged at him, so he
finished his whiskey and picked up Stellan's glass. It was a gift,
and Pierce didn't take back gifts. If Stellan wasn't going to drink
it, no one would. Pierce walked it to his bathroom and poured it
down the drain. The Atlas' water filtration system would detect it
as a contaminant and jettison it out to space.</p>
<p class="western3">Pierce walked back into his office where he
sat in his creaking chair behind his antiquated desk. His hand
absently fumbled for the bottom drawer and pulled it open. From it,
he retrieved a single bullet. He held it close to his eyes and
tumbled it in his fingers like an alien object full of wonder,
curious about the way the light gleamed across its metal skin.</p>
<p class="western3">"Like always," he said.</p>
<p class="c10"><span class="c11">Nine</span></p>
<p class="western3">Pierce entered the bridge, his footsteps on
the glass walkway announcing his presence to Arlo and Evans, who
quickly hid a game they were playing against each other using an
application they had illegally installed on their links. They
closed the application and turned to their workstations just as
Pierce stopped on his command platform and leaned on the railing in
front of it, his shoulder blades poking up under his heavy
officer's coat.</p>
<p class="western3">"Light drive spun up and ready to fire,
Captain," Arlo said. "Say the word, and I'll kick off the training
wheels." The Atlas breathed as the steady fire of the thrusters
propelled them forward and the light drive swelled.</p>
<p class="western3">"Destination solution achieved," Evans said.
"Coordinates locked. Scans indicate no anomalies. Should be smooth
sailing, Captain."</p>
<p class="western3">Pierce looked down upon the two young men on
his bridge, one who'd barely escaped his teens. The danger they
accepted to fly in space and break the law of relativity was
minimal. In the history of space exploration and mining, there had
only been one ship lost. At least, there had only been one ship
they knew of. The price they would pay for a mistake would be
absolute. One miscalculation, and the whole ship and everyone
aboard would perish. More than that, it would be like they just
ceased to exist. They wouldn't have time to process the end, and no
one on New Earth would know what happened to them. They just
wouldn't return home. Perhaps they flew into a star or too close to
a singularity. Perhaps they slammed into an unmonitored asteroid.
Perhaps the light drive glitched and warped the ship along with
space.</p>
<p class="western3">The dangers were infinite. Pierce saw a
thousand different anomalies and variables, and not all of them
were in the deep black. However, the chances were remote that any
harm would come to them from out there. Statistically, with all the
thorough protocols and redundancies, they were more likely to die
in a fire or earthquake on New Earth. They were safe here.</p>
<p class="western3">The only dangers that concerned him were
within the Atlas' hull.</p>
<p class="western3">Arlo and Evans looked up to Pierce looming
on his platform; he did not see the impatience they clearly bore.
He saw dependence.</p>
<p class="western3">He thought about the Atlas and its civilian
crew. He never considered himself a civilian. He wasn't delusional.
He was cognizant he had left the military years ago, and he
presumed he accepted the command of the Atlas as a form of
retirement, graceful or not. He presumed Stellan had joined him on
this ship for similar reasons, though Stellan's reasons were
probably more for seeking escape, as he still had so much life left
to live. They all did.</p>
<p class="western3">He had faith in Stellan. He had faith in
himself. Trust. He knew they could handle any challenge they would
encounter, and he knew they'd all come home, one last triumph from
the greatest beyond from which any man could ever return.</p>
<p class="western3">He took an oath to himself then, and it
transcended every oath he'd ever taken. He'd sworn to uphold peace
and order once. He'd sworn to serve humanity as captain of the
Atlas. He'd maintain both of those oaths, but above all else, he'd
do what was necessary if the time came. He would accept the
consequences and never look back. If that meant defying the
Council's agent, so be it; necessity was the only justification he
needed.</p>
<p class="western3">"Captain?" Arlo said.</p>
<p class="western3">"All right, then. That's it," Pierce said.
Standing straight, he took a deep breath, his wide chest billowing
like a balloon. "Start the countdown."</p>
<p class="western3">Arlo's voice boomed throughout the ship as
he announced the Atlas would jump and counted down from ten. The
pulse of the light drive grew. The sound changed in volume and
intensity but not pitch. The pitch never changed, and Pierce
considered that was the reason it was so maddening. It echoed a
perfectly constant frequency, like being tied down while water
drops tapped your forehead. It was impossible to escape.</p>
<p class="western3">"Seven, six, five..."</p>
<p class="western3">A steady stream of something in the air
flowed over them. It might have been static electricity, but their
hair didn't stand on end. It might have been like the sudden
magnetic tightening of the deck when the tram arrived at the
platform, but there were no knocks of the stabilizing paddles. All
sound became distant. The bridge expanded, as did the distance
between each of them.</p>
<p class="western3">"Three, two, one…"</p>
<p class="western3">They all knew these effects were no trick of
perception. The light drive warped space around them. It would draw
their destination closer while pushing their point of origin
farther behind them. It was how they were able to travel faster
than light.</p>
<p class="western3">"Zero."</p>
<p class="western3">"Mark," Pierce said.</p>
<p class="western3">Arlo pressed a window on his workstation.
The light drive silenced. They could only hear themselves breathe.
Then everything inside the hull of the Atlas snapped back into
place, returning to normal, even the mechanical rise and fall of
the light drive.</p>
<p class="western3">The Atlas trembled and then streaked across
the stars faster than light, disappearing into the great expanse of
the deep, dark black.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// tmp_403fa671f87995d2e5aedda9ecddad25_61bGyM.ch.fixed.fc.tidied.stylehacked.xfixed_split_006.vue?2c99dd68